import React, { useState, useEffect } from 'react';
import {
  Grid,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  TextField,
  InputAdornment,
  Box,
  Chip,
  Container,
  IconButton,
  CircularProgress,
} from '@material-ui/core';
import {
  Search as SearchIcon,
  Code as CodeIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Build as BuildIcon,
  Security as SecurityIcon,
  CloudQueue as CloudIcon,
  Storage as StorageIcon,
  Web as WebIcon,
} from '@material-ui/icons';
import { Content, Link } from '@backstage/core-components';
import { BackstageTheme } from '@backstage/theme';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { TemplateEntityV1beta3 } from '@backstage/plugin-scaffolder-common';
import { AzureDevOpsIcon } from '../icons';
import { RocketLaunchIcon } from '../icons/RocketLaunchIcon';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme: BackstageTheme) => ({
  root: {
    padding: theme.spacing(3),
    backgroundColor: theme.palette.background.default,
  },
  header: {
    marginBottom: theme.spacing(4),
    padding: theme.spacing(3, 4),
    backgroundColor: theme.palette.background.paper,
    borderRadius: 16,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
    position: 'relative',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: 4,
      background: '#FFDD00',
    },
  },
  headerIcon: {
    width: 80,
    height: 80,
    borderRadius: 12,
    backgroundColor: '#FFDD00',
    color: theme.palette.text.primary,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: theme.spacing(3),
    flexShrink: 0,
    '& svg': {
      fontSize: '2.5rem',
    },
  },
  headerContent: {
    flex: 1,
  },
  title: {
    fontWeight: 700,
    fontSize: '2.2rem',
    marginBottom: theme.spacing(0.5),
    letterSpacing: '-0.02em',
    color: theme.palette.text.primary,
  },
  subtitle: {
    fontSize: '1rem',
    fontWeight: 400,
    lineHeight: 1.4,
    color: theme.palette.text.secondary,
    margin: 0,
  },
  searchBox: {
    marginBottom: theme.spacing(3),
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.palette.background.paper,
      borderRadius: 12,
      '& fieldset': {
        borderColor: theme.palette.divider,
      },
      '&:hover fieldset': {
        borderColor: theme.palette.primary.main,
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.primary.main,
      },
    },
  },
  templateCard: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: 20,
    border: `1px solid ${theme.palette.divider}`,
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    position: 'relative',
    overflow: 'hidden',
    backgroundColor: theme.palette.background.paper,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
    '&:hover': {
      transform: 'translateY(-6px)',
      boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)',
      borderColor: theme.palette.primary.main,
      '& $templateIcon': {
        transform: 'scale(1.05)',
      },
    },
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: 4,
      background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
    },
  },
  templateHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
  },
  templateIcon: {
    width: 64,
    height: 64,
    borderRadius: 16,
    backgroundColor: 'transparent',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: theme.spacing(2),
    transition: 'transform 0.3s ease',
    '& svg': {
      fontSize: '3rem',
      color: theme.palette.primary.main,
    },
  },
  templateTitle: {
    fontWeight: 700,
    fontSize: '1.4rem',
    color: theme.palette.text.primary,
    flex: 1,
    lineHeight: 1.3,
  },
  templateDescription: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(3),
    lineHeight: 1.6,
    fontSize: '0.95rem',
    minHeight: '3em',
    display: '-webkit-box',
    '-webkit-line-clamp': 3,
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
  },
  tagsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    marginBottom: theme.spacing(2),
  },
  tag: {
    backgroundColor: theme.palette.action.hover,
    fontSize: '0.75rem',
    height: 24,
  },
  cardActions: {
    padding: theme.spacing(2),
    paddingTop: 0,
    marginTop: 'auto',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  useTemplateButton: {
    borderRadius: '12px !important',
    textTransform: 'none !important',
    fontWeight: '600 !important',
    fontSize: '1rem !important',
    padding: `${theme.spacing(1.5, 3)} !important`,
    backgroundColor: '#FFDD00 !important',
    color: '#000000 !important',
    border: 'none !important',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1) !important',
    transition: 'all 0.2s ease !important',
    '&:hover': {
      backgroundColor: '#FFD700 !important',
      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15) !important',
      transform: 'translateY(-1px) !important',
    },
    '&:active': {
      backgroundColor: '#FFC700 !important',
    },
    '&:focus': {
      backgroundColor: '#FFDD00 !important',
      outline: 'none !important',
    },
    '&:disabled': {
      backgroundColor: '#FFDD00 !important',
      color: '#000000 !important',
      opacity: '1 !important',
    },
    '&.Mui-disabled': {
      backgroundColor: '#FFDD00 !important',
      color: '#000000 !important',
      opacity: '1 !important',
    },
  },
  categorySection: {
    marginBottom: theme.spacing(4),
  },
  categoryTitle: {
    fontWeight: 700,
    fontSize: '1.6rem',
    marginBottom: theme.spacing(3),
    color: theme.palette.text.primary,
    display: 'flex',
    alignItems: 'center',
    '& svg': {
      marginRight: theme.spacing(1.5),
      fontSize: '1.8rem',
      color: theme.palette.primary.main,
    },
  },
  bannerContainer: {
    '& > div': {
      minHeight: 'auto',
    }
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 200,
  },
  errorContainer: {
    textAlign: 'center',
    padding: theme.spacing(4),
    backgroundColor: theme.palette.background.paper,
    borderRadius: 12,
    border: `1px solid ${theme.palette.error.light}`,
    margin: theme.spacing(2, 0),
  },
}));

interface Template {
  id: string;
  title: string;
  description: string;
  tags: string[];
  category: string;
  icon: React.ReactNode;
  starred: boolean;
  path: string;
  entity: TemplateEntityV1beta3;
}

// Helper function to get icon based on template metadata
const getTemplateIcon = (template: TemplateEntityV1beta3): React.ReactNode => {
  const tags = template.metadata.tags || [];
  const kind = template.spec?.type || '';
  
  if (tags.includes('azure-devops') || tags.includes('azure')) {
    return <AzureDevOpsIcon width={24} height={24} />;
  }
  if (tags.includes('react') || tags.includes('frontend') || kind.includes('website')) {
    return <WebIcon />;
  }
  if (tags.includes('api') || tags.includes('service') || kind.includes('service')) {
    return <StorageIcon />;
  }
  if (tags.includes('infrastructure') || tags.includes('infra') || tags.includes('terraform')) {
    return <BuildIcon />;
  }
  if (tags.includes('security') || tags.includes('compliance')) {
    return <SecurityIcon />;
  }
  if (tags.includes('cloud') || tags.includes('kubernetes')) {
    return <CloudIcon />;
  }
  
  return <CodeIcon />;
};

// Helper function to get category based on template metadata
const getTemplateCategory = (template: TemplateEntityV1beta3): string => {
  const tags = template.metadata.tags || [];
  const kind = template.spec?.type || '';
  
  if (tags.includes('azure-devops') || tags.includes('azure')) {
    return 'Azure DevOps';
  }
  if (tags.includes('react') || tags.includes('frontend') || kind.includes('website')) {
    return 'Frontend';
  }
  if (tags.includes('api') || tags.includes('service') || kind.includes('service')) {
    return 'APIs & Servicios';
  }
  if (tags.includes('infrastructure') || tags.includes('infra') || tags.includes('terraform')) {
    return 'Infraestructura';
  }
  if (tags.includes('security') || tags.includes('compliance')) {
    return 'Seguridad';
  }
  if (tags.includes('data') || tags.includes('analytics')) {
    return 'Datos';
  }
  
  return 'General';
};

// Helper function to determine if template should be starred
const isTemplateStarred = (template: TemplateEntityV1beta3): boolean => {
  const tags = template.metadata.tags || [];
  return tags.includes('azure-devops') || tags.includes('featured') || tags.includes('recommended');
};

export const CreateComponent: React.FC = () => {
  const classes = useStyles();
  const catalogApi = useApi(catalogApiRef);
  const [searchTerm, setSearchTerm] = useState('');
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [starredTemplates, setStarredTemplates] = useState<Set<string>>(
    new Set([])
  );

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        setLoading(true);
        const response = await catalogApi.getEntities({
          filter: {
            kind: 'Template',
          },
        });

        const formattedTemplates: Template[] = response.items.map((entity) => {
          const template = entity as TemplateEntityV1beta3;
          const tags = template.metadata.tags || [];
          const title = template.metadata.title || template.metadata.name;
          const description = template.metadata.description || 'No description available';
          
          return {
            id: `${template.metadata.namespace || 'default'}/${template.metadata.name}`,
            title,
            description,
            tags,
            category: getTemplateCategory(template),
            icon: getTemplateIcon(template),
            starred: isTemplateStarred(template),
            path: `/create/templates/${template.metadata.namespace || 'default'}/${template.metadata.name}`,
            entity: template,
          };
        });

        setTemplates(formattedTemplates);
        
        // Set initially starred templates
        const initialStarred = new Set(
          formattedTemplates
            .filter(t => t.starred)
            .map(t => t.id)
        );
        setStarredTemplates(initialStarred);
        
      } catch (err) {
        console.error('Error fetching templates:', err);
        setError('Error loading templates. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchTemplates();
  }, [catalogApi]);

  const filteredTemplates = templates.filter((template: Template) =>
    template.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    template.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    template.tags.some((tag: string) => tag.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const toggleStar = (templateId: string) => {
    const newStarred = new Set(starredTemplates);
    if (newStarred.has(templateId)) {
      newStarred.delete(templateId);
    } else {
      newStarred.add(templateId);
    }
    setStarredTemplates(newStarred);
  };

  const groupedTemplates = filteredTemplates.reduce((acc: Record<string, Template[]>, template: Template) => {
    if (!acc[template.category]) {
      acc[template.category] = [];
    }
    acc[template.category].push(template);
    return acc;
  }, {} as Record<string, Template[]>);

  return (
    <div>
      <Content className={classes.root}>
        <Container maxWidth="lg">
          {/* Header */}
          <div className={classes.header}>
            <div className={classes.headerIcon}>
              <CodeIcon />
            </div>
            <div className={classes.headerContent}>
              <Typography variant="h3" component="h1" className={classes.title}>
                Template Catalog
              </Typography>
              <Typography variant="subtitle1" className={classes.subtitle}>
                Unified control panel to access business templates and development tools
              </Typography>
            </div>
          </div>

          {/* Search */}
          <Box className={classes.searchBox}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search for templates by name, technology, or use case..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Loading State */}
          {loading && (
            <Box className={classes.loadingContainer}>
              <CircularProgress size={60} />
              <Typography variant="h6" style={{ marginTop: 16 }}>
                Cargando templates...
              </Typography>
            </Box>
          )}

          {/* Error State */}
          {error && (
            <Box className={classes.errorContainer}>
              <Typography variant="h6" color="error">
                Error al cargar templates
              </Typography>
              <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
                {error}
              </Typography>
            </Box>
          )}

          {/* Templates by Category */}
          {!loading && !error && Object.entries(groupedTemplates).map(([category, categoryTemplates]) => (
            <div key={category} className={classes.categorySection}>
              <Typography variant="h5" className={classes.categoryTitle}>
                <CodeIcon />
                {category}
              </Typography>
              
              <Grid container spacing={3}>
                {categoryTemplates.map((template: Template) => (
                  <Grid item xs={12} sm={6} md={4} key={template.id}>
                    <Card className={classes.templateCard}>
                      <CardContent>
                        <div className={classes.templateHeader}>
                          <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                            <div className={classes.templateIcon}>
                              {template.icon}
                            </div>
                            <Typography variant="h6" className={classes.templateTitle}>
                              {template.title}
                            </Typography>
                          </div>
                          <IconButton
                            size="small"
                            onClick={() => toggleStar(template.id)}
                            color="primary"
                          >
                            {starredTemplates.has(template.id) ? (
                              <StarIcon />
                            ) : (
                              <StarBorderIcon />
                            )}
                          </IconButton>
                        </div>

                        <Typography variant="body2" className={classes.templateDescription}>
                          {template.description}
                        </Typography>

                        <div className={classes.tagsContainer}>
                          {template.tags.map((tag: string) => (
                            <Chip
                              key={tag}
                              label={tag}
                              size="small"
                              className={classes.tag}
                            />
                          ))}
                        </div>
                      </CardContent>

                      <CardActions className={classes.cardActions}>
                        <Link 
                          to={template.path} 
                          style={{ 
                            textDecoration: 'none', 
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            backgroundColor: '#FFDD00',
                            color: '#000000',
                            fontWeight: 600,
                            textTransform: 'none',
                            borderRadius: 12,
                            padding: '12px 24px',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                            border: 'none',
                            fontSize: '1rem',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            fontFamily: 'inherit'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#FFD700';
                            e.currentTarget.style.transform = 'translateY(-1px)';
                            e.currentTarget.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.15)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#FFDD00';
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                          }}
                        >
                          {/* <RocketLaunchIcon 
                            size={18} 
                            color="#000000"
                          /> */}
                          Create
                        </Link>
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </div>
          ))}

          {!loading && !error && filteredTemplates.length === 0 && (
            <Box textAlign="center" py={4}>
              <Typography variant="h6" color="textSecondary">
                No templates found matching your search
              </Typography>
              <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
                Try different terms or explore our categories
              </Typography>
            </Box>
          )}
        </Container>
      </Content>
    </div>
  );
};
